import { Controller } from "@hotwired/stimulus"

// Agenda only. Its clock must never decide whether the video is live or restart it.
export default class extends Controller {
  static targets = ["agenda", "card", "preview", "previewStatus", "previewHeading", "previewContent", "standby", "closing"]
  static values = { schedule: Array, timeZone: String }

  dayAt(time) {
    return this.dayFormat.format(time)
  }
  connect() {
    this.broadcastOffline = false
    this.dayFormat = new Intl.DateTimeFormat("en-CA", { timeZone: this.timeZoneValue, year: "numeric", month: "2-digit", day: "2-digit" })
    this.dateFormat = new Intl.DateTimeFormat("en-US", { timeZone: this.timeZoneValue, year: "numeric", month: "long", day: "numeric" })
    this.sessions = this.scheduleValue.map(session => ({
      ...session, startsAt: Date.parse(session.startsAt), endsAt: Date.parse(session.endsAt)
    }))
    const days = new Map()
    for (const session of this.sessions) {
      const date = this.dayAt(session.startsAt)
      const day = days.get(date)
      if (day) {
        day.endsAt = Math.max(day.endsAt, session.endsAt)
      } else {
        days.set(date, { date, startsAt: session.startsAt, endsAt: session.endsAt })
      }
    }
    this.days = Array.from(days.values())
    this.selectedId = null
    this.previewContentTarget.replaceChildren()
    this.update()
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  broadcastStateChanged({ detail: { offline } }) {
    this.broadcastOffline = offline
    this.update()
  }

  sessionAt(now) {
    return this.sessions.find(session => session.startsAt <= now && now < session.endsAt) ||
      this.sessions.find(session => session.startsAt > now)
  }

  update() {
    if (!this.sessions) return
    clearTimeout(this.timer)
    const now = Date.now()
    const selected = this.sessionAt(now)
    const changed = selected?.id !== this.selectedId
    this.selectedId = selected?.id
    this.updatePreview(now, selected)

    this.cardTargets.forEach((card, index) => {
      const session = this.sessions[index]
      const current = session.id === selected?.id
      const past = now >= session.endsAt
      const status = card.querySelector("[data-congress-status]")
      card.dataset.past = String(past)
      if (current) card.setAttribute("aria-current", "true")
      else card.removeAttribute("aria-current")
      status.textContent = past ? "Earlier" : current ? `${now >= session.startsAt ? "On stage" : "Up next"} · ${card.dataset.day}` : ""
      status.hidden = !status.textContent
      if (current && changed) {
        this.previewContentTarget.replaceChildren(card.querySelector("[data-congress-preview]").content.cloneNode(true))
        const anchor = card.dataset.firstInDay === "true" ? card.previousElementSibling : card
        this.agendaTarget.scrollTo({
          left: this.agendaTarget.scrollLeft + anchor.getBoundingClientRect().left - this.agendaTarget.getBoundingClientRect().left,
          behavior: "instant"
        })
      }
    })
    const delay = Math.min(30_000, ...this.sessions
      .flatMap(session => [session.startsAt, session.endsAt])
      .filter(time => time > now).map(time => time - now))
    this.timer = setTimeout(() => this.update(), delay)
  }

  updatePreview(now, selected) {
    const today = this.dayAt(now)
    const day = this.days.find(day => day.date === today)
    const finished = this.days.length > 0 && this.days.every(day => now >= day.endsAt)
    const dayFinished = day && now >= day.endsAt && (!selected || now < selected.startsAt)
    const showClosing = finished && this.broadcastOffline
    const dayClosing = dayFinished && !finished

    this.closingTarget.hidden = !showClosing
    this.previewTarget.hidden = showClosing
    this.standbyTarget.hidden = Boolean(showClosing || dayClosing)
    this.previewHeadingTarget.hidden = !dayClosing
    this.previewHeadingTarget.textContent = dayClosing ? "That’s a wrap for today." : ""

    if (finished) {
      this.previewStatusTarget.textContent = "World Congress North America"
      this.previewContentTarget.textContent = this.dateFormat.formatRange(this.days[0].startsAt, this.days.at(-1).startsAt)
    } else if (selected) {
      // Calendar arithmetic on Pacific date labels avoids DST and browser-timezone offsets.
      const tomorrow = new Date(`${today}T12:00:00Z`)
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)
      this.previewStatusTarget.textContent = dayFinished && this.dayAt(selected.startsAt) === tomorrow.toISOString().slice(0, 10)
        ? "Tomorrow"
        : now >= selected.startsAt ? "Scheduled now" : "Up next"
    } else {
      this.previewStatusTarget.textContent = ""
      this.previewContentTarget.replaceChildren()
    }
  }
}
